import moment from "moment";
import {
  calendarYmdInTimeZone,
  formatZonedDate,
} from "./timezone";

export const formatPhone = (value: string, previousValue?: string) => {
  if (!value) {
    return value;
  }
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (!previousValue || value.length > previousValue.length) {
    if (cvLength < 4) {
      return currentValue;
    }
    if (cvLength < 7) {
      return `(${currentValue.slice(0, 3)})${currentValue.slice(3)}`;
    }
    return `(${currentValue.slice(0, 3)}) ${currentValue.slice(
      3,
      6
    )}-${currentValue.slice(6, 10)}`;
  }
};

export function formatCurrencyValue(value: any) {
  return (+value || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    currency: "USD",
    style: "currency",
  });
}
export const formatCurrency = (params: any) =>
  formatCurrencyValue(params.value);

// Format currency showing absolute value (for XGrid valueFormatter)
export const formatAbsCurrency = (params: { value: unknown }) =>
  formatCurrencyValue(Math.abs(params.value as number));

// Format profit with parentheses for negative values (for XGrid valueFormatter)
export const formatProfitCurrency = (params: { value: unknown }) => {
  const value = params.value as number;
  return value < 0
    ? `(${formatCurrencyValue(Math.abs(value))})`
    : formatCurrencyValue(value);
};

export const formatDollars = (amount: number) => {
  if (isNaN(amount)) {
    return "";
  }
  return `${amount < 0 ? "-" : ""}$${Math.abs(amount).toLocaleString(
    undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  )}`;
};

function toValidDate(dt: any): Date | null {
  if (dt == null) return null;
  if (dt instanceof Date && !isNaN(dt.getTime())) return dt;
  const m = moment(dt);
  if (!m.isValid()) return null;
  return m.toDate();
}

/**
 * Formats a date for display or export. When `timeZone` is set (user profile zone),
 * the calendar fields are interpreted in that zone; otherwise moment uses local time.
 */
export const formatDate = (
  dt: any,
  format: string = "MM/DD/yyyy",
  timeZone?: string
) => {
  const date = toValidDate(dt);
  if (!date) return "";
  if (!timeZone) {
    return moment(dt).format(format);
  }
  const f = format.toUpperCase();
  if (f === "YYYY-MM-DD") {
    return calendarYmdInTimeZone(date, timeZone);
  }
  if (f === "MM/DD/YYYY") {
    return formatZonedDate(date, timeZone, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }
  if (f === "MMM D, YYYY") {
    return formatZonedDate(date, timeZone, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return formatZonedDate(date, timeZone);
};
